/**
 * TransferPaymentModal.jsx
 *
 * Shown to a carrier when:
 *   (a) they have an unread payment_pending notification (popup on page load)
 *   (b) they click "Upload Receipt" on a payment_pending bid in My Bids
 *
 * Displays:
 *  - Congratulation header
 *  - Bank wire / ACH details (copyable)
 *  - Amount due
 *  - Receipt upload (image / PDF)
 *  - Status tracker: Payment Pending → Receipt Sent → Unlocked
 */

import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  X, Copy, Check, Upload, FileText, Loader2, CheckCircle2,
  AlertCircle, Unlock, ArrowRight, Minus, ChevronLeft, ChevronRight,
} from "lucide-react";
import { api } from "../../lib/api";

const ACCENT = "#FF4D00";
const GOLD   = "#F5C842";
const CARD   = "#161618";

const BANK = {
  "Bank Name":        "First Century Bank",
  "Bank Address":     "1731 N Elm St, Commerce, GA 30529 USA",
  "Routing (ABA)":    "061120084",
  "Account Number":   "4030000547082",
  "Account Type":     "CHECKING",
  "SWIFT Code":       "FCNSUS32",
  "Beneficiary Name": "Andrej Sasaroga",
};

function CopyRow({ label, value }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <div className="flex items-center justify-between gap-3 py-2.5"
      style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
      <span className="font-head text-[9px] uppercase tracking-[0.2em] shrink-0" style={{ color: "rgba(255,255,255,0.3)", minWidth: 110 }}>{label}</span>
      <span className="font-mono text-xs font-bold text-white flex-1 text-right break-words">{value}</span>
      <button type="button" onClick={copy}
        className="p-1.5 transition-colors hover:text-white shrink-0"
        style={{ color: copied ? "#22C55E" : "rgba(255,255,255,0.25)" }}>
        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      </button>
    </div>
  );
}

function StatusStep({ n, label, active, done }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-6 h-6 shrink-0 flex items-center justify-center font-head text-[10px] font-black transition-all"
        style={{ background: done ? "#22C55E" : active ? ACCENT : "rgba(255,255,255,0.06)", color: done || active ? "white" : "rgba(255,255,255,0.2)" }}>
        {done ? <Check className="w-3 h-3" /> : n}
      </div>
      <span className="text-xs font-head uppercase tracking-[0.1em] transition-colors"
        style={{ color: done ? "#22C55E" : active ? "white" : "rgba(255,255,255,0.2)" }}>{label}</span>
    </div>
  );
}

export function TransferPaymentModal({ notification, bidId, driverName, placementFee, paymentOption, discount = 0, token, onClose, onMinimize, onReceiptUploaded, existingStatus, currentIndex = 0, total = 1, onPrev, onNext }) {
  const [uploading, setUploading] = useState(false);
  const [uploaded,  setUploaded]  = useState(existingStatus === "receipt_uploaded");
  const [dragOver,  setDragOver]  = useState(false);
  const fileRef = useRef();

  const finalDue = Math.max(0, (placementFee || 0) - (discount || 0));
  const isReceiptUploaded = uploaded || existingStatus === "receipt_uploaded";
  const isTransferred     = existingStatus === "transferred";

  async function handleFile(file) {
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/heic", "application/pdf"];
    if (!allowed.includes(file.type)) { toast.error("Please upload an image (JPG/PNG/WEBP) or PDF"); return; }
    if (file.size > 15 * 1024 * 1024)  { toast.error("File too large — max 15MB"); return; }

    setUploading(true);
    try {
      const targetBidId = bidId || notification?.bid_id;
      await api.marketUploadPaymentReceipt(token, targetBidId, file);
      setUploaded(true);
      toast.success("Receipt uploaded — awaiting admin confirmation");
      if (notification?.notification_id) {
        await api.marketMarkNotificationRead(token, notification.notification_id).catch(() => {});
      }
      if (onReceiptUploaded) onReceiptUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed — please try again");
    } finally { setUploading(false); }
  }

  const driver = driverName || notification?.driver_name || "the driver";
  const fee    = finalDue || notification?.placement_fee || 0;
  const payOpt = paymentOption || notification?.payment_option || "standard";

  return (
    <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.85)" }}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.div className="w-full max-w-lg overflow-y-auto max-h-[92vh]"
        style={{ background: "#0A0A0A", border: `1px solid ${isTransferred ? "#22C55E40" : GOLD + "30"}` }}
        initial={{ scale: 0.93, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}>

        {/* ── Multi-transfer navigation ── */}
        {total > 1 && (
          <div className="flex items-center justify-between px-4 py-2.5"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}>
            <button onClick={onPrev} disabled={currentIndex === 0}
              className="p-1 transition-colors disabled:opacity-25 hover:text-white"
              style={{ color: "rgba(255,255,255,0.4)" }}>
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="font-head text-[9px] uppercase tracking-[0.25em]" style={{ color: "rgba(255,255,255,0.35)" }}>
              {currentIndex + 1} of {total} unpaid
            </span>
            <button onClick={onNext} disabled={currentIndex === total - 1}
              className="p-1 transition-colors disabled:opacity-25 hover:text-white"
              style={{ color: "rgba(255,255,255,0.4)" }}>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Header */}
        <div className="relative px-6 pt-6 pb-5"
          style={{ background: isTransferred ? "rgba(34,197,94,0.05)" : "rgba(245,200,66,0.05)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          {/* Minimize/close button */}
          {onMinimize && !isTransferred ? (
            <button onClick={onMinimize}
              title="Minimize — badge stays visible until payment is confirmed"
              className="absolute top-4 right-4 p-1.5 transition-colors hover:text-white"
              style={{ color: "rgba(255,255,255,0.3)" }}>
              <Minus className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={onClose} className="absolute top-4 right-4 p-1.5 transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.3)" }}>
              <X className="w-4 h-4" />
            </button>
          )}
          {isTransferred ? (
            <>
              <div className="flex items-center gap-2 mb-2">
                <Unlock className="w-4 h-4" style={{ color: "#22C55E" }} />
                <p className="font-head text-[9px] uppercase tracking-[0.3em]" style={{ color: "#22C55E" }}>Profile Unlocked</p>
              </div>
              <h2 className="font-head text-xl font-black text-white">Access Granted for {driver}</h2>
              <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.4)" }}>Full profile, contact info, and direct messaging are now available.</p>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4" style={{ color: GOLD }} />
                <p className="font-head text-[9px] uppercase tracking-[0.3em]" style={{ color: GOLD }}>Transfer Approved</p>
              </div>
              <h2 className="font-head text-xl font-black text-white">Your bid on {driver} was approved!</h2>
              <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.4)" }}>Complete the ACH wire transfer below to unlock the driver&apos;s full profile.</p>
            </>
          )}
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Status tracker */}
          <div className="flex items-center gap-0">
            <StatusStep n="1" label="Bid Approved" done={true} />
            <div className="flex-1 h-px mx-2" style={{ background: isReceiptUploaded || isTransferred ? GOLD : "rgba(255,255,255,0.08)" }} />
            <StatusStep n="2" label="Payment Sent" active={!isReceiptUploaded && !isTransferred} done={isReceiptUploaded || isTransferred} />
            <div className="flex-1 h-px mx-2" style={{ background: isTransferred ? "#22C55E" : "rgba(255,255,255,0.08)" }} />
            <StatusStep n="3" label="Profile Unlocked" active={isReceiptUploaded && !isTransferred} done={isTransferred} />
          </div>

          {/* Amount due */}
          {!isTransferred && (
            <div className="flex items-center justify-between px-4 py-3"
              style={{ background: "rgba(245,200,66,0.06)", border: "1px solid rgba(245,200,66,0.2)" }}>
              <div>
                <p className="font-head text-[8px] uppercase tracking-[0.25em]" style={{ color: "rgba(245,200,66,0.6)" }}>Amount Due</p>
                <p className="font-head text-2xl font-black" style={{ color: GOLD }}>${fee.toLocaleString()}</p>
                {discount > 0 && <p className="text-[10px]" style={{ color: "rgba(255,255,255,0.3)" }}>One-time discount applied: -${discount.toLocaleString()}</p>}
              </div>
              <div className="text-right">
                <p className="font-head text-[8px] uppercase tracking-[0.2em]" style={{ color: "rgba(255,255,255,0.2)" }}>Payment Plan</p>
                <p className="text-xs font-bold text-white capitalize">{payOpt.replace(/_/g, " ")}</p>
              </div>
            </div>
          )}

          {/* Bank wire details */}
          {!isTransferred && (
            <div>
              <p className="font-head text-[9px] uppercase tracking-[0.3em] mb-2" style={{ color: "rgba(255,255,255,0.3)" }}>ACH / Wire Transfer Details</p>
              <div className="px-4 py-1" style={{ background: CARD, border: `1px solid rgba(245,200,66,0.15)`, borderLeft: `2px solid ${GOLD}` }}>
                {Object.entries(BANK).map(([k, v]) => <CopyRow key={k} label={k} value={v} />)}
              </div>
            </div>
          )}

          {/* Receipt upload */}
          {!isTransferred && (
            <div>
              <p className="font-head text-[9px] uppercase tracking-[0.3em] mb-2" style={{ color: "rgba(255,255,255,0.3)" }}>
                {isReceiptUploaded ? "Receipt Submitted" : "Upload Payment Receipt"}
              </p>
              {isReceiptUploaded ? (
                <div className="flex items-center gap-3 px-4 py-3" style={{ background: "rgba(34,197,94,0.07)", border: "1px solid rgba(34,197,94,0.25)" }}>
                  <CheckCircle2 className="w-5 h-5 shrink-0" style={{ color: "#22C55E" }} />
                  <div>
                    <p className="text-sm font-bold text-white">Receipt uploaded successfully</p>
                    <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }}>Admin is reviewing your payment. You&apos;ll get an in-app notification when access is granted.</p>
                  </div>
                </div>
              ) : (
                <>
                  <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ""; }} />
                  <div
                    onClick={() => !uploading && fileRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
                    className="flex flex-col items-center justify-center py-10 cursor-pointer transition-all"
                    data-testid="receipt-upload-area"
                    style={{ border: `2px dashed ${dragOver ? GOLD : "rgba(255,255,255,0.12)"}`, background: dragOver ? "rgba(245,200,66,0.04)" : "transparent" }}>
                    {uploading ? (
                      <>
                        <Loader2 className="w-8 h-8 mb-3 animate-spin" style={{ color: GOLD }} />
                        <p className="text-sm" style={{ color: "rgba(255,255,255,0.5)" }}>Uploading receipt…</p>
                      </>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 mb-3" style={{ color: "rgba(255,255,255,0.2)" }} />
                        <p className="text-sm font-bold text-white">Drop receipt here or click to browse</p>
                        <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.3)" }}>JPG, PNG, WEBP, PDF — max 15MB</p>
                      </>
                    )}
                  </div>
                  <p className="text-[10px] mt-2" style={{ color: "rgba(255,255,255,0.2)" }}>
                    Upload a screenshot or PDF of your bank transfer confirmation. Your profile access will unlock once admin verifies it.
                  </p>
                </>
              )}
            </div>
          )}

          {isTransferred && (
            <div className="flex flex-col items-center py-4 text-center gap-3">
              <Unlock className="w-10 h-10" style={{ color: "#22C55E" }} />
              <p className="text-sm" style={{ color: "rgba(255,255,255,0.6)" }}>
                Full profile, phone number, email address, employment history, and direct messaging with {driver} are now available in your <strong className="text-white">My Drivers</strong> tab.
              </p>
            </div>
          )}

          {/* Close / Minimize */}
          <button
            onClick={onMinimize && !isTransferred ? onMinimize : onClose}
            data-testid="payment-modal-close"
            className="w-full py-2.5 font-head text-xs uppercase tracking-[0.2em] font-black transition-all"
            style={{ background: isTransferred ? "rgba(34,197,94,0.1)" : "rgba(255,255,255,0.05)", color: isTransferred ? "#22C55E" : "rgba(255,255,255,0.5)", border: `1px solid ${isTransferred ? "rgba(34,197,94,0.3)" : "rgba(255,255,255,0.1)"}` }}>
            {isTransferred
              ? "Close — View My Drivers"
              : isReceiptUploaded
                ? "Minimize — Waiting for Admin Approval"
                : onMinimize ? "Minimize for Now —" : "Close"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
